/**
 * F16.B: React context carrying the resolved `tenantBranding` row for a
 * `/c/${clientSlug}` portal. The parent route loader fetches the row from
 * Convex; this provider exposes it to descendants via `useTenantBranding`.
 *
 * Outside a portal (e.g. on `/`, `/trust/*`), the hook throws so callers
 * who accidentally read it from non-portal code fail loudly rather than
 * showing stale state.
 *
 * See DEMO-PLAN.md §F16.B and decisions.md §D9.
 */

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Shape of the row returned by `api.tenantBranding.getBySlug`. Mirrored
 * here so this module does not import from `convex/_generated` (which would
 * pull the SPA's tsc program into convex sources unnecessarily).
 */
export interface TenantBranding {
  clientSlug: string;
  displayName: string;
  logoStorageId: string | null | undefined;
  primaryColor: string;
  lockedTenantId: string;
  lockedSiteIds: readonly string[];
  firmSkillsSiteId: string | null | undefined;
  firmSkillsDriveId: string | null | undefined;
  demoSiteName: string;
  practiceArea: 'corporate-ma' | 'litigation-disputes';
  theme: 'art-deco' | 'conservative-serif';
  showDemo: boolean;
}

export interface TenantBrandingContextValue {
  branding: TenantBranding;
  slug: string;
}

const TenantBrandingContext = createContext<TenantBrandingContextValue | null>(
  null,
);

export function TenantBrandingProvider(props: {
  branding: TenantBranding;
  children: ReactNode;
}) {
  const value: TenantBrandingContextValue = {
    branding: props.branding,
    slug: props.branding.clientSlug,
  };
  return (
    <TenantBrandingContext.Provider value={value}>
      {props.children}
    </TenantBrandingContext.Provider>
  );
}

/**
 * Hook to read the current portal branding. Throws if called outside a
 * `<TenantBrandingProvider>`. Use `useOptionalTenantBranding` if you need
 * a soft check (e.g. components that render in both portal and default
 * contexts).
 */
export function useTenantBranding(): TenantBrandingContextValue {
  const value = useContext(TenantBrandingContext);
  if (value === null) {
    throw new Error(
      'useTenantBranding called outside of <TenantBrandingProvider>. ' +
        'This component is only valid inside a `/c/${slug}` route subtree.',
    );
  }
  return value;
}

/** Same as `useTenantBranding` but returns null instead of throwing. */
export function useOptionalTenantBranding(): TenantBrandingContextValue | null {
  return useContext(TenantBrandingContext);
}
